import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUserMd, FaPlus, FaEdit, FaTrash, FaSpinner, FaUsers, FaTimes, FaCheck, FaClock, FaFileAlt, FaShieldAlt, FaStethoscope, FaUserPlus, FaExchangeAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/adminAPI';

const DoctorManagement = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [verifying, setVerifying] = useState(null);

  // Client Assignment
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedDoctorForClients, setSelectedDoctorForClients] = useState(null);
  const [doctorClients, setDoctorClients] = useState([]);
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [assigning, setAssigning] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    verified: '',
    specialization: '',
    search: '',
  });

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    licenseNumber: '',
    licenseState: '',
    licenseExpiry: '',
    registrationNumber: '',
    specialization: '',
    qualifications: '',
    yearsExperience: '',
    bio: '',
    consultationFee: '',
    commissionPercentage: '',
  });

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    const fetchFilteredDoctors = async () => {
      try {
        const data = await adminAPI.getAllDoctors(filters);
        setDoctors(data);
      } catch (error) {
        console.error('Failed to fetch doctors:', error);
      }
    };

    if (!loading) {
      fetchFilteredDoctors();
    }
  }, [filters, loading]);

  const fetchDoctors = async () => {
    try {
      const data = await adminAPI.getAllDoctors({});
      setDoctors(data);
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
      toast.error('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      licenseNumber: '',
      licenseState: '',
      licenseExpiry: '',
      registrationNumber: '',
      specialization: '',
      qualifications: '',
      yearsExperience: '',
      bio: '',
      consultationFee: '',
      commissionPercentage: '',
    });
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setSelectedDoctor(null);
    setShowModal(true);
  };

  const openEditModal = (doctor) => {
    setFormData({
      firstName: doctor.firstName || '',
      lastName: doctor.lastName || '',
      email: doctor.email || '',
      password: '',
      phone: doctor.phone || '',
      licenseNumber: doctor.licenseNumber || '',
      licenseState: doctor.licenseState || '',
      licenseExpiry: doctor.licenseExpiry || '',
      registrationNumber: doctor.registrationNumber || '',
      specialization: doctor.specialization || '',
      qualifications: doctor.qualifications || '',
      yearsExperience: doctor.yearsExperience || '',
      bio: doctor.bio || '',
      consultationFee: doctor.consultationFee || '',
      commissionPercentage: doctor.commissionPercentage || '',
    });
    setModalMode('edit');
    setSelectedDoctor(doctor);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (modalMode === 'create') {
        const newDoctor = await adminAPI.createDoctor(formData);
        setDoctors([...doctors, newDoctor]);
        toast.success('Doctor created successfully');
      } else {
        const { email, password, ...updateData } = formData;
        const updatedDoctor = await adminAPI.updateDoctor(selectedDoctor.id, updateData);
        setDoctors(doctors.map(d => d.id === selectedDoctor.id ? updatedDoctor : d));
        toast.success('Doctor updated successfully');
      }
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(error.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (doctorId) => {
    if (!window.confirm('Are you sure you want to delete this doctor? All client assignments will be completed.')) {
      return;
    }

    setDeleting(doctorId);
    try {
      await adminAPI.deleteDoctor(doctorId);
      setDoctors(doctors.filter(d => d.id !== doctorId));
      toast.success('Doctor deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete doctor');
    } finally {
      setDeleting(null);
    }
  };

  const handleVerify = async (doctorId, verified) => {
    setVerifying(doctorId);
    try {
      const updatedDoctor = await adminAPI.verifyDoctor(doctorId, { isVerified: verified, notes: '' });
      setDoctors(doctors.map(d => d.id === doctorId ? updatedDoctor : d));
      toast.success(verified ? 'Doctor verified successfully' : 'Doctor verification removed');
    } catch (error) {
      console.error('Verify failed:', error);
      toast.error('Failed to update verification status');
    } finally {
      setVerifying(null);
    }
  };

  // Client Assignment Functions
  const openClientModal = async (doctor) => {
    setSelectedDoctorForClients(doctor);
    setShowClientModal(true);
    setLoadingClients(true);
    try {
      const [assignedClients, availableClients] = await Promise.all([
        adminAPI.getDoctorClients(doctor.id),
        adminAPI.getUnassignedDoctorClients({})
      ]);
      setDoctorClients(assignedClients);
      setUnassignedClients(availableClients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const handleAssignClient = async (clientId) => {
    setAssigning(clientId);
    try {
      const assignedClient = await adminAPI.assignDoctorToClient(clientId, selectedDoctorForClients.id);
      // Move client from unassigned to assigned
      setUnassignedClients(unassignedClients.filter(c => c.id !== clientId));
      setDoctorClients([...doctorClients, assignedClient]);
      // Update doctor's client count
      setDoctors(doctors.map(d =>
        d.id === selectedDoctorForClients.id
          ? { ...d, activeClientCount: (d.activeClientCount || 0) + 1 }
          : d
      ));
      toast.success('Client assigned successfully');
    } catch (error) {
      console.error('Assign failed:', error);
      toast.error(error.message || 'Failed to assign client');
    } finally {
      setAssigning(null);
    }
  };

  const handleUnassignClient = async (clientId) => {
    setAssigning(clientId);
    try {
      const unassignedClient = await adminAPI.assignDoctorToClient(clientId, null);
      // Move client from assigned to unassigned
      const clientToMove = doctorClients.find(c => c.id === clientId);
      setDoctorClients(doctorClients.filter(c => c.id !== clientId));
      if (clientToMove) {
        setUnassignedClients([...unassignedClients, clientToMove]);
      }
      // Update doctor's client count
      setDoctors(doctors.map(d =>
        d.id === selectedDoctorForClients.id
          ? { ...d, activeClientCount: Math.max((d.activeClientCount || 1) - 1, 0) }
          : d
      ));
      toast.success('Client unassigned successfully');
    } catch (error) {
      console.error('Unassign failed:', error);
      toast.error('Failed to unassign client');
    } finally {
      setAssigning(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Stats
  const totalDoctors = doctors.length;
  const verifiedDoctors = doctors.filter(d => d.isVerified).length;
  const pendingVerification = doctors.filter(d => !d.isVerified).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <FaShieldAlt className="text-2xl text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500">Manage coaches, clients, doctors, and documents</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 bg-white rounded-xl p-2 shadow-lg overflow-x-auto">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            <FaFileAlt />
            Documents
          </Link>
          <Link
            to="/admin/coaches"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            <FaUserMd />
            Coaches
          </Link>
          <Link
            to="/admin/clients"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            <FaUsers />
            Clients
          </Link>
          <Link
            to="/admin/doctors"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-primary-600 text-white whitespace-nowrap"
          >
            <FaStethoscope />
            Doctors
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FaStethoscope className="text-xl text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Doctors</p>
              <p className="text-2xl font-bold text-gray-900">{totalDoctors}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <FaCheck className="text-xl text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Verified</p>
              <p className="text-2xl font-bold text-green-600">{verifiedDoctors}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <FaClock className="text-xl text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending Verification</p>
              <p className="text-2xl font-bold text-amber-600">{pendingVerification}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.verified}
                onChange={(e) => setFilters({ ...filters, verified: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Doctors</option>
                <option value="true">Verified Only</option>
                <option value="false">Pending Verification</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
              <input
                type="text"
                placeholder="e.g., Endocrinology"
                value={filters.specialization}
                onChange={(e) => setFilters({ ...filters, specialization: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Name or email..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={openCreateModal}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                <FaPlus />
                Add Doctor
              </button>
            </div>
          </div>
        </div>

        {/* Doctors Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <FaSpinner className="animate-spin text-3xl text-gray-400" />
          </div>
        ) : doctors.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <FaStethoscope className="text-5xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Doctors Yet</h3>
            <p className="text-gray-500 mb-6">Add your first doctor to get started</p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              <FaPlus />
              Add Doctor
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {doctors.map((doctor) => (
              <div
                key={doctor.id}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {doctor.firstName?.charAt(0)}{doctor.lastName?.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Dr. {doctor.firstName} {doctor.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{doctor.email}</p>
                    </div>
                  </div>
                  {doctor.isVerified ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <FaCheck className="mr-1" /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      <FaClock className="mr-1" /> Pending
                    </span>
                  )}
                </div>

                {doctor.specialization && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {doctor.specialization}
                    </span>
                  </div>
                )}

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <p><span className="font-medium">License:</span> {doctor.licenseNumber} ({doctor.licenseState})</p>
                  <p><span className="font-medium">Expires:</span> {formatDate(doctor.licenseExpiry)}</p>
                  {doctor.yearsExperience && (
                    <p><span className="font-medium">Experience:</span> {doctor.yearsExperience} years</p>
                  )}
                </div>

                {doctor.bio && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{doctor.bio}</p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <button
                    onClick={() => openClientModal(doctor)}
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <FaUserPlus />
                    <span>{doctor.activeClientCount || 0} Clients</span>
                  </button>
                  <div className="flex items-center gap-2">
                    {!doctor.isVerified && (
                      <button
                        onClick={() => handleVerify(doctor.id, true)}
                        disabled={verifying === doctor.id}
                        className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Verify"
                      >
                        {verifying === doctor.id ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                      </button>
                    )}
                    <button
                      onClick={() => openEditModal(doctor)}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(doctor.id)}
                      disabled={deleting === doctor.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {deleting === doctor.id ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                    </button>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-400">
                  Added {formatDate(doctor.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                  {modalMode === 'create' ? 'Add New Doctor' : 'Edit Doctor'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Personal Info - email lookup for create, name fields for edit */}
                {modalMode === 'create' ? (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                      <strong>Note:</strong> The doctor must first register an account at <strong>/register</strong>. Enter their registered email below to assign them the Doctor role and add their medical credentials.
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Doctor's Registered Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="doctor@example.com"
                      />
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* License Info */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">License Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        License Number *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.licenseNumber}
                        onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., MH-12345"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        License State *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.licenseState}
                        onChange={(e) => setFormData({ ...formData, licenseState: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., Maharashtra"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        License Expiry *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.licenseExpiry}
                        onChange={(e) => setFormData({ ...formData, licenseExpiry: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registration Number
                      </label>
                      <input
                        type="text"
                        value={formData.registrationNumber}
                        onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="MCI/State Council No."
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Info */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Professional Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specialization
                      </label>
                      <input
                        type="text"
                        value={formData.specialization}
                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., Endocrinology"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Years of Experience
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.yearsExperience}
                        onChange={(e) => setFormData({ ...formData, yearsExperience: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Qualifications
                    </label>
                    <input
                      type="text"
                      value={formData.qualifications}
                      onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., MBBS, MD (Endocrinology)"
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bio
                    </label>
                    <textarea
                      rows={3}
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Brief description about the doctor..."
                    />
                  </div>
                </div>

                {/* Fees */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Fees & Commission</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Consultation Fee (INR)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.consultationFee}
                        onChange={(e) => setFormData({ ...formData, consultationFee: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., 500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Commission %
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.commissionPercentage}
                        onChange={(e) => setFormData({ ...formData, commissionPercentage: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., 15"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Saving...
                      </>
                    ) : modalMode === 'create' ? (
                      'Create Doctor'
                    ) : (
                      'Update Doctor'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Client Assignment Modal */}
        {showClientModal && selectedDoctorForClients && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Manage Clients</h2>
                  <p className="text-sm text-gray-500">
                    Dr. {selectedDoctorForClients.firstName} {selectedDoctorForClients.lastName}
                  </p>
                </div>
                <button
                  onClick={() => setShowClientModal(false)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {loadingClients ? (
                  <div className="flex items-center justify-center py-12">
                    <FaSpinner className="animate-spin text-3xl text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Assigned Clients */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FaUsers className="text-primary-500" />
                        Assigned Clients ({doctorClients.length})
                      </h3>
                      {doctorClients.length === 0 ? (
                        <p className="text-gray-500 text-sm bg-gray-50 p-4 rounded-xl">
                          No clients assigned to this doctor yet.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {doctorClients.map((client) => (
                            <div
                              key={client.id}
                              className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center text-green-700 font-bold">
                                  {client.firstName?.charAt(0)}{client.lastName?.charAt(0)}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {client.firstName} {client.lastName}
                                  </p>
                                  <p className="text-sm text-gray-500">{client.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {client.planType && (
                                  <span className="px-2 py-1 text-xs font-medium bg-white text-gray-700 rounded-lg">
                                    {client.planType}
                                  </span>
                                )}
                                <button
                                  onClick={() => handleUnassignClient(client.id)}
                                  disabled={assigning === client.id}
                                  className="p-2 text-red-500 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                                  title="Unassign"
                                >
                                  {assigning === client.id ? (
                                    <FaSpinner className="animate-spin" />
                                  ) : (
                                    <FaTimes />
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Unassigned Clients */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FaUserPlus className="text-amber-500" />
                        Available Clients ({unassignedClients.length})
                      </h3>
                      {unassignedClients.length === 0 ? (
                        <p className="text-gray-500 text-sm bg-gray-50 p-4 rounded-xl">
                          No unassigned clients available.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {unassignedClients.map((client) => (
                            <div
                              key={client.id}
                              className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl hover:border-primary-300 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold">
                                  {client.firstName?.charAt(0)}{client.lastName?.charAt(0)}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {client.firstName} {client.lastName}
                                  </p>
                                  <p className="text-sm text-gray-500">{client.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {client.planType && (
                                  <span className="px-2 py-1 text-xs font-medium bg-white text-gray-700 rounded-lg border">
                                    {client.planType}
                                  </span>
                                )}
                                <button
                                  onClick={() => handleAssignClient(client.id)}
                                  disabled={assigning === client.id}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                                >
                                  {assigning === client.id ? (
                                    <FaSpinner className="animate-spin" />
                                  ) : (
                                    <>
                                      <FaPlus className="text-xs" />
                                      Assign
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => setShowClientModal(false)}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DoctorManagement;
